<template>
  <div class="annual-report-page" ref="reportPage">
    <!-- 顶部操作栏 -->
    <div class="action-bar" data-html2canvas-ignore="true">
      <button class="back-btn" @click="$router.back()"> 返回</button>
      <button class="export-btn" @click="exportImage" :disabled="isExporting">
        {{ isExporting ? '生成中...' : '导出年报' }}
      </button>
    </div>

    <!-- 👇 1. 全屏地图与照片层 -->
    <div class="map-bg">
      <div class="photo-layer">
        <div 
          class="polaroid" 
          v-for="(photo, index) in displayPhotos" 
          :key="index"
          :class="'p-' + (index + 1)"
          @click="!photo.isDefault && openPhotoSelector(index)"
          :style="{ cursor: photo.isDefault ? 'default' : 'pointer' }"
        >
          <img :src="photo.url || defaultPhoto" alt="回忆" />
          <div class="pin"></div>
          <div class="city-name">📍 {{ photo.city }}</div>
        </div>
      </div>
    </div>

    <!-- 👇 2. 三列文字区 -->
    <div class="report-container">
      <div class="report-left">
        <div class="left-fixed-title">今年你的印记：</div>
        <div class="main-data">
          <div class="data-row"><span class="num">{{ stats.all.provinces }}</span><span class="unit">个省份</span></div>
          <div class="data-row"><span class="num">{{ stats.all.cities }}</span><span class="unit">座城市</span></div>
        </div>
        <div class="percent-box">
          <div class="percent-label">你的足迹已点亮全国</div>
          <div class="percent-value">{{ stats.all.percent }}<span class="symbol">%</span></div>
          <div class="percent-sub-label">的城市</div>
        </div>
        <div class="favorite-box">
          <div class="favorite-label">今年，你最偏爱的地方是</div>
          <div class="favorite-city">「 {{ stats.all.favoriteCity }} 」</div>
        </div>
      </div>

      <div class="report-center">
        <!-- 年份与金句（水平并排） -->
        <div class="top-year-title">
          <div class="title-left">
            <h1 class="year-title">{{ currentYear }}</h1>
            <div class="year-sub">年度印记 <span class="site-url">aiyinji.cn</span></div>
          </div>
          <div class="center-history-text">
            你已累计在全国 <span class="big-red">{{ stats.history.percent }}%</span> 城市留下爱的印记
          </div>
        </div>
        <!-- Slogan -->
        <div class="slogan-wrapper">
          <img src="@/assets/seal.webp" class="seal-img" alt="印章" />
          <div class="center-title">{{ randomSlogan }}</div>
        </div>
      </div>

      <div class="report-right">
        <div class="group-section">
          <h3>你与他们同行的足迹</h3>
          <ul>
            <li v-for="(grp, idx) in stats.groups" :key="idx">
              <span class="tag">{{ grp.name }}</span>
              <span class="group-desc">走过 {{ grp.cities }} 座城市，占全国 {{ grp.percent }}%</span>
            </li>
            <li v-if="stats.groups.length === 0" class="empty-tip">你今年没有小组记录</li>
          </ul>
        </div>
        <div class="history-section">
          <p class="history-slogan">从相遇到现在，<br/>你在「爱的印记」珍藏了...</p>
          <div class="history-data">
            <div class="h-item"><span>{{ stats.history.provinces }}</span> 省</div>
            <div class="h-divider">·</div>
            <div class="h-item"><span>{{ stats.history.cities }}</span> 市</div>
            <div class="h-divider">·</div>
            <div class="h-item"><span>{{ stats.history.footprints }}</span> 段回忆</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 越权访问提示弹窗 -->
    <div v-if="showAuthTip" class="tip-mask" @click.self="goBackToCenter">
      <div class="tip-panel" @click.stop>
        <div class="tip-text">年度印记报告每年12月下旬生成，12月底至次年2月为报告获取期。当前不在获取期内，敬请期待岁末年初的专属回顾。</div>
        <button class="tip-btn" @click="goBackToCenter">确定</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getFootprintList, getMyGroupList } from '@/api/footprint'
import html2canvas from 'html2canvas'
import defCover1 from '@/assets/defaults/def-cover-1.jpg'
import defCover2 from '@/assets/defaults/def-cover-2.jpg'
import defFood from '@/assets/defaults/def-food.jpg'
import defHotel from '@/assets/defaults/def-hotel.jpg'
import defScene1 from '@/assets/defaults/def-scene-1.jpg'
import defScene2 from '@/assets/defaults/def-scene-2.jpg'

export default {
  name: 'AnnualReport',
  data() {
    const now = new Date();
    let reportYear = now.getFullYear();
    const month = now.getMonth() + 1; 
    if (month === 1 || month === 2) reportYear -= 1;
    
    return {
      isExporting: false,
      showAuthTip: false,
      currentYear: reportYear,
      defaultPhoto: 'https://picsum.photos/100/120',
      defaultImgs: [defCover1, defCover2, defFood, defHotel, defScene1, defScene2],
      randomSlogan: '',
      stats: {
        all: { provinces: 0, cities: 0, percent: 0, favoriteCity: '待定' },
        groups: [],
        history: { provinces: 0, cities: 0, footprints: 0 }
      },
      displayPhotos: [],
      showPhotoModal: false,
      selectingIndex: -1,
      selectingCity: '',
      availableCityPhotos: [],
      rawFootprints: [],
      rawGroups: []
    }
  },
  async mounted() {
    const now = new Date();
    const month = now.getMonth() + 1;
    const date = now.getDate();
    
    let isOpen = false;
    if (month === 12 && date >= 25) isOpen = true; 
    if (month === 1 || month === 2) isOpen = true; 

    if (!isOpen) {
      this.showAuthTip = true;
      return;
    }
    this.setRandomSlogan()
    await this.fetchDataAndCalculate()
  },

  methods: {
    goBackToCenter() {
      this.$router.replace('/personal-center');
    },
    setRandomSlogan() {
      const slogans = [
        "山河漫踏千程，爱意藏于岁月。",
        "纵使流年辗转，足迹永记陪伴。",
        "不社交，不炫耀，独留温柔珍藏。",
        "你能看到的，只有你，和你爱的人。",
        "步履丈量陪伴，山河封存深情。"
      ]
      this.randomSlogan = slogans[Math.floor(Math.random() * slogans.length)]
    },
    async fetchDataAndCalculate() {
      try {
        const [allFootprints, groups] = await Promise.all([ getFootprintList(), getMyGroupList() ])
        this.rawFootprints = allFootprints || []
        this.rawGroups = groups || []

        const yearFootprints = this.rawFootprints.filter(item => {
          const timeStr = item.arriveTime || item.createTime;
          if (!timeStr) return false;
          if (typeof timeStr === 'number') return new Date(timeStr).getFullYear() === this.currentYear;
          return String(timeStr).substring(0, 4) === String(this.currentYear);
        })

        const allProvs = new Set(yearFootprints.map(i => i.province).filter(Boolean))
        const allCities = new Set(yearFootprints.map(i => i.city).filter(Boolean))
        
        const cityCount = {}
        yearFootprints.forEach(i => { if (i.city) cityCount[i.city] = (cityCount[i.city] || 0) + 1 })
        let maxCount = 0; let favCities = []
        for (const [city, count] of Object.entries(cityCount)) {
          if (count > maxCount) { maxCount = count; favCities = [city] } 
          else if (count === maxCount) { favCities.push(city) }
        }
        const favStr = favCities.slice(0, 3).join(' · ') || '未知'

        this.stats.all = {
          provinces: allProvs.size, cities: allCities.size,
          percent: ((allCities.size / 333) * 100).toFixed(1), favoriteCity: favStr
        }

        const groupStats = []
        const yearGroupFoots = yearFootprints.filter(i => i.belongGroupId)
        const groupedByGroup = {}
        yearGroupFoots.forEach(item => {
          if (!groupedByGroup[item.belongGroupId]) groupedByGroup[item.belongGroupId] = []
          groupedByGroup[item.belongGroupId].push(item)
        })
        for (const [gid, foots] of Object.entries(groupedByGroup)) {
          const groupInfo = this.rawGroups.find(g => g.groupId === gid)
          if (groupInfo) {
            const gCities = new Set(foots.map(i => i.city).filter(Boolean))
            groupStats.push({ name: groupInfo.groupName, cities: gCities.size, percent: ((gCities.size / 333) * 100).toFixed(1) })
          }
        }
        groupStats.sort((a, b) => b.cities - a.cities)
        this.stats.groups = groupStats.slice(0, 5)

        const hisProvs = new Set(this.rawFootprints.map(i => i.province).filter(Boolean))
        const hisCities = new Set(this.rawFootprints.map(i => i.city).filter(Boolean))
        this.stats.history = {
          provinces: hisProvs.size, cities: hisCities.size, footprints: this.rawFootprints.length,
          percent: ((hisCities.size / 333) * 100).toFixed(1)
        }

        let finalPhotos = []
        const yearPhotoFoots = yearFootprints.filter(i => i.photoList && i.photoList.length > 0)
        yearPhotoFoots.sort(() => 0.5 - Math.random())
        yearPhotoFoots.forEach(f => { if (finalPhotos.length < 16) finalPhotos.push({ city: f.city, url: f.photoList[0], allImgs: f.photoList }) })
        
        const historyPhotoFoots = this.rawFootprints.filter(i => i.photoList && i.photoList.length > 0)
        historyPhotoFoots.sort(() => 0.5 - Math.random())
        if (finalPhotos.length < 16) {
          historyPhotoFoots.forEach(f => {
            if (finalPhotos.length < 16 && !finalPhotos.some(p => p.city === f.city)) finalPhotos.push({ city: f.city, url: f.photoList[0], allImgs: f.photoList })
          })
        }

        if (finalPhotos.length < 16) {
          const needed = 16 - finalPhotos.length
          for (let i = 0; i < needed; i++) {
            finalPhotos.push({ city: '待解锁', url: this.defaultImgs[i % this.defaultImgs.length], isDefault: true })
          }
        }

        finalPhotos.sort(() => 0.5 - Math.random())
        this.displayPhotos = finalPhotos
      } catch (err) { 
        console.error("年报数据加载失败", err)
      }
    },
    async openPhotoSelector(index) {
      const photoObj = this.displayPhotos[index]
      if (!photoObj || !photoObj.city) return
      this.selectingIndex = index
      this.selectingCity = photoObj.city
      let allCityImages = []
      this.rawFootprints.forEach(item => {
        if (item.city === photoObj.city && item.photoList && item.photoList.length > 0) allCityImages = allCityImages.concat(item.photoList)
      })
      this.availableCityPhotos = [...new Set(allCityImages)]
      if (this.availableCityPhotos.length > 0) this.showPhotoModal = true
      else alert(`你还没有上传过【${photoObj.city}】的照片`)
    },
    confirmPhotoSelect(imgUrl) {
      this.displayPhotos[this.selectingIndex].url = imgUrl
      this.showPhotoModal = false
    },
    async exportImage() {
      this.isExporting = true
      const el = this.$refs.reportPage
      try {
        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#F7F3E9' })
        const dataUrl = canvas.toDataURL('image/png')
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `爱的印记_${this.currentYear}年度报告.png`
        link.click()
      } catch (err) { console.error("导出失败", err) } finally { this.isExporting = false }
    }
  }
}
</script>

<style scoped>
/* ================= 基础布局 ================= */
.annual-report-page {
  width: 100vw; height: 100vh;
  background-image: url("~@/assets/subtle.webp"); background-color: #F7F3E9;
  background-repeat: repeat; background-size: 400px auto;
  position: relative; overflow: hidden; color: #333;
}

/* 顶部按钮 */
.action-bar { position: absolute; top: 2vh; left: 2vw; display: flex; gap: clamp(8px, 1.5vw, 12px); z-index: 100; }
.back-btn, .export-btn { height: clamp(24px, 3.2vh, 28px); padding: 0 clamp(10px, 1.5vw, 16px); border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; font-weight: 600; font-size: clamp(11px, 1.1vw, 14px); cursor: pointer; text-shadow: 0 1px 2px rgba(0,0,0,0.15); flex-shrink: 0; transition: all 0.2s ease; }
.back-btn { background: linear-gradient(to bottom, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.15)); color: #fff; border: 1px solid rgba(255, 255, 255, 0.8); box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 2px 4px rgba(255, 255, 255, 0.9); }
.export-btn { color: #fff; border: 1px solid rgba(255, 255, 255, 0.4); background: linear-gradient(to bottom, rgba(255,255,255,0.25), rgba(255,255,255,0) 50%), rgba(214, 72, 107, 0.85); box-shadow: 0 4px 10px rgba(214, 72, 107, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.5); }
.back-btn:hover, .export-btn:hover:not(:disabled) { transform: translateY(-1px); }

/* ================= 👇 全屏地图与百分比画板 ================= */
.map-bg {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 100vw;
  height: 100vh;
  
  background: url('~@/assets/china-map.png') no-repeat center center;
  background-size: contain; 
  opacity: 1;               
  z-index: 1; 
  pointer-events: none;
}

/* 照片层 */
.photo-layer {
  position: absolute;
  inset: 0;
}

/* 拍立得相框 */
.polaroid {
  position: absolute;
  background: #fff;
  width: 12vmin; 
  padding: 0.8vmin 0.8vmin 3vmin 0.8vmin; 
  
  box-shadow: 0 8px 20px rgba(0,0,0,0.18);
  border-radius: 2px;
  transition: all 0.3s ease;
  cursor: pointer;
  pointer-events: auto;
  transform: translate(-50%, -50%);
}
.polaroid:hover { transform: translate(-50%, -50%) scale(1.15) rotate(0deg) !important; z-index: 50; }
.polaroid img { width: 100%; height: auto; aspect-ratio: 1/1; object-fit: cover; display: block; }

.pin { position: absolute; top: -10%; left: 50%; transform: translateX(-50%); width: 12%; height: 12%; background: radial-gradient(circle at 30% 30%, #ff6b6b, #900); border-radius: 50%; box-shadow: 1px 2px 3px rgba(0,0,0,0.4); }
.city-name { position: absolute; bottom: 0.5%; left: 0; width: 100%; text-align: center; font-size: clamp(8px, 1.2vmin, 12px); color: #333; font-weight: bold; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;}

/* 👇 16 个点位 */
.p-1 { top: 35%; left: 25%; transform: translate(-50%, -50%) rotate(-8deg); } 
.p-2 { top: 55%; left: 50%; transform: translate(-50%, -50%) rotate(5deg); } 
.p-3 { top: 28%; left: 72%; transform: translate(-50%, -50%) rotate(-4deg); } 
.p-4 { top: 70%; left: 65%; transform: translate(-50%, -50%) rotate(10deg); }
.p-5 { top: 36%; left: 62%; transform: translate(-50%, -50%) rotate(-12deg); }
.p-6 { top: 38%; left: 64%; transform: translate(-50%, -50%) rotate(6deg); }
.p-7 { top: 42%; left: 42%; transform: translate(-50%, -50%) rotate(-7deg); }
.p-8 { top: 45%; left: 30%; transform: translate(-50%, -50%) rotate(8deg); }
.p-9 { top: 50%; left: 40%; transform: translate(-50%, -50%) rotate(-5deg); }
.p-10 { top: 48%; left: 54%; transform: translate(-50%, -50%) rotate(5deg); }
.p-11 { top: 54%; left: 64%; transform: translate(-50%, -50%) rotate(-9deg); }
.p-12 { top: 58%; left: 54%; transform: translate(-50%, -50%) rotate(7deg); }
.p-13 { top: 62%; left: 34%; transform: translate(-50%, -50%) rotate(-10deg); }
.p-14 { top: 65%; left: 44%; transform: translate(-50%, -50%) rotate(4deg); }
.p-15 { top: 68%; left: 58%; transform: translate(-50%, -50%) rotate(-6deg); }
.p-16 { top: 74%; left: 48%; transform: translate(-50%, -50%) rotate(9deg); }

/* ================= 文字容器 ================= */
.report-container {
  width: 100vw; 
  height: 88vh;
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  display: flex; 
  justify-content: space-between; 
  box-sizing: border-box;
  z-index: 10; 
  pointer-events: none;
}

.report-left {
  pointer-events: auto;
  flex: 0 0 250px; 
  display: flex; 
  flex-direction: column;
  justify-content: space-between; 
  padding-top: 6vh; 
  padding-bottom: 6vh;
  padding-left: 4vw; 
  box-sizing: border-box;
  z-index: 50; 
}

.report-right {
  pointer-events: auto;
  flex: 0 0 250px; 
  display: flex; 
  flex-direction: column;
  justify-content: space-between; 
  padding-top: 12vh; 
  padding-bottom: 6vh; 
  
  padding-right: 4vw; 
  margin-right: 0;
  align-items: flex-end; 
  text-align: left; 
  margin-left: auto; 
  z-index: 50; 
}

.report-center {
  flex: 1; position: relative; display: flex; flex-direction: column; align-items: center; justify-content: space-between; padding: 4vh 0; pointer-events: none;
}
.top-year-title, .slogan-wrapper { pointer-events: auto; }

/* ================= 左侧字体 ================= */
.left-fixed-title { font-size: clamp(14px, 1.4vw, 18px); color: #c49a37; font-weight: bold; }
.main-data { display: flex; flex-direction: column; gap: 0.5vh; margin: 1vh 0;  margin-top: -5vh; margin-bottom: 1vh;}
.data-row .num { font-size: clamp(28px, 3.5vw, 46px); font-weight: bold; color: #d6486b; font-family: "Georgia", serif; margin-right: 6px; }
.data-row .unit { font-size: clamp(12px, 1.2vw, 15px); color: #333; font-weight: bold; }
.percent-box { padding-left: 12px; border-left: 4px solid #c49a37; margin: 1vh 0; }
.percent-label { font-size: clamp(10px, 0.9vw, 12px); color: #333; }
.percent-value { font-size: clamp(28px, 3.5vw, 46px); font-weight: bold; color: #d6486b; font-family: "Georgia", serif; line-height: 1.1; margin: 2px 0; }
.percent-sub-label { font-size: clamp(12px, 1.1vw, 14px); color: #333; font-weight: bold; }
.favorite-label { font-size: clamp(10px, 0.9vw, 12px); color: #333; }
.favorite-city { font-size: clamp(14px, 1.3vw, 18px); font-weight: bold; color: #2c3e50; margin-top: 4px; line-height: 1.4; word-break: break-all; }

/* ================= 右侧字体 ================= */
.group-section h3 { 
  font-size: clamp(13px, 1.2vw, 16px); 
  color: #10b981;
  margin-bottom: 1.5vh; 
  font-weight: bold; 
  text-align: left; 
  width: 100%; 
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji" !important;
}
.group-section ul { list-style: none; padding: 0; margin: 0; width: 100%;}
.group-section li { margin-bottom: 1.2vh; display: flex; flex-direction: column; gap: 2px; align-items: flex-start; }
.group-section .group-desc { 
  font-size: clamp(10px, 0.9vw, 12px); 
  color: #333; 
  margin-left: 2px; 
  line-height: 1.4;
  word-break: break-all;
}
.group-section .tag { 
  background: rgba(214, 72, 107, 0.1); 
  color: #d6486b; 
  padding: 2px 8px; 
  border-radius: 4px; 
  font-size: clamp(10px, 0.9vw, 12px); 
  align-self: flex-start; 
}

.history-slogan { 
  font-size: clamp(12px, 1.1vw, 14px); 
  color: #333; 
  line-height: 1.5; 
  margin-bottom: 0.5vh;
  text-align: left; 
  width: 100%; 
}
.history-data { 
  display: flex; 
  align-items: baseline; 
  gap: clamp(2px, 0.5vw, 6px); 
  flex-wrap: nowrap; /* 关键：绝对不允许换行 */
  margin-top: 1.5vh; 
  justify-content: flex-end; 
  width: 100%; 
}
.history-data .h-item { 
  font-size: clamp(11px, 1vw, 13px); 
  color: #333; 
  display: flex;
  align-items: baseline; 
  gap: 2px;
}
.history-data .h-item span { 
  font-size: clamp(22px, 2.2vw, 28px); 
  color: #d6486b; 
  font-weight: 900; 
  font-family: "Georgia", serif; 
  line-height: 1; 
  margin: 0 2px;   
  letter-spacing: -1px; 
}

/* ================= 顶部与底部 ================= */
.top-year-title {
  position: absolute; 
  top: -4vh; 
  left: 50%; 
  transform: translateX(-50%); 
  z-index: 30; 
  width: max-content; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  gap: clamp(10px, 2vw, 20px); 
}
.title-left {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.top-year-title .year-title { font-size: clamp(46px, 6vw, 80px); color: #d6486b; margin: 0; font-family: "Georgia", serif; line-height: 0.9; text-shadow: 0 2px 10px rgba(214, 72, 107, 0.15); }
.top-year-title .year-sub { 
  font-size: clamp(12px, 1.2vw, 16px); 
  color: #10b981; 
  letter-spacing: 6px; 
  margin-top: 4px; 
  position: relative;
  left: 9vw; 
}
.site-url {
  letter-spacing: 0px; 
  font-size: clamp(12px, 1.2vw, 16px);   
  font-family: Arial, sans-serif; 
  margin-left: 4px; 
  opacity: 0.9; 
}

.center-history-text { 
  text-align: left; 
  font-size: clamp(12px, 1.2vw, 15px); 
  color: #444; 
  line-height: 1.4; 
  font-weight: bold; 
  white-space: nowrap; 
  text-shadow: 0 2px 4px #fff, 0 0 8px #fff;
}
.center-history-text .big-red { color: #d6486b; font-size: clamp(18px, 2vw, 26px); font-family: "Georgia", serif; margin: 0 2px; }

.slogan-wrapper {
  position: absolute;
  bottom: -3vh; 
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  display: flex; 
  justify-content: center; 
  align-items: center; 
  gap: clamp(10px, 2vw, 24px); 
  width: max-content; 
}
.seal-img { width: clamp(36px, 5vw, 50px); height: clamp(36px, 5vw, 50px); object-fit: contain;  filter: drop-shadow(0 4px 6px rgba(0,0,0,0.2)); pointer-events: none; }
.center-title { 
  text-align: center; 
  font-size: clamp(16px, 1.8vw, 24px); 
  color: #d6486b; 
  font-weight: bold; 
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Microsoft YaHei", sans-serif !important;
  pointer-events: none; 
  line-height: 1.4; 
  white-space: nowrap; 
  text-shadow: 
    -1.5px -1.5px 0 #fff,  
     1.5px -1.5px 0 #fff,
    -1.5px  1.5px 0 #fff,
     1.5px  1.5px 0 #fff,
     0px 4px 6px rgba(0,0,0,0.1); 
}

/* 弹窗 */
.tip-mask { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.32); z-index: 999; display: flex; align-items: center; justify-content: center; }
.tip-panel { min-width: 260px; max-width: 320px; padding: 24px; background-image: url("~@/assets/subtle.webp"); background-repeat: repeat; background-size: 420px auto; background-position: center; background-color: rgba(247, 243, 233, 0.98); border-radius: 22px; text-align: center; box-shadow: 0 10px 30px rgba(0,0,0,0.18); }
.tip-text { font-size: 13px; color: #383838; margin-bottom: 22px; line-height: 1.4; }
.tip-btn { padding: 8px 26px; border: none; background: #d14a4a; color: #ffffff; border-radius: 8px; font-size:16px; }
.tip-btn:active { opacity:0.85; }

.group-section .empty-tip { 
  /* 👇 核心：匹配右侧正文的字体大小，并调淡颜色 */
  font-size: clamp(11px, 1.1vw, 13px); 
  color: #888; 
  font-weight: normal;
  line-height: 1.5;
  text-align: left; /* 保证和上面的标题左对齐 */
  margin-top: 1vh; /* 和上面的标题稍微拉开一点距离 */
}
</style>